// Renderiza un formulario a partir de un FormSchema + valores. Los valores
// resultantes se guardan en `favorite_items.details.type_parameters`.

import type { FormSchema, FormSchemaField } from '@/lib/api';
import { fieldCls, checkboxCls } from '@/lib/form-styles';

export type DynamicFormValue = string | number | boolean | null;
export type DynamicFormValues = Record<string, DynamicFormValue>;

export interface DynamicFormProps {
  schema: FormSchema;
  values: DynamicFormValues;
  onChange: (next: DynamicFormValues) => void;
  disabled?: boolean;
}

/**
 * Render dinamico de un form. Cada `FormSchemaField` se mapea a un input
 * HTML segun `type`. La validacion HTML5 (required, type=url, type=number)
 * se aplica nativamente al submit del form contenedor.
 */
export default function DynamicForm({
  schema,
  values,
  onChange,
  disabled = false,
}: DynamicFormProps): JSX.Element {
  function set(key: string, value: DynamicFormValue): void {
    onChange({ ...values, [key]: value });
  }

  if (schema.fields.length === 0) {
    return (
      <p className="text-sm text-ink-muted italic">
        Este tipo no define campos custom. Solo se piden nombre y descripcion.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {schema.fields.map((field) => (
        <Field key={field.key} field={field} value={values[field.key] ?? null} onChange={set} disabled={disabled} />
      ))}
    </div>
  );
}

function Field({
  field,
  value,
  onChange,
  disabled,
}: {
  field: FormSchemaField;
  value: DynamicFormValue;
  onChange: (key: string, value: DynamicFormValue) => void;
  disabled: boolean;
}): JSX.Element {
  // El prop del componente ya se llama `field`, asi que el helper entra con
  // nombre propio. Un solo alias local para los cinco tipos de control del
  // switch de abajo: todos son campos de un mismo formulario y comparten fila.
  const baseInputCls = fieldCls({ size: 'lg' });
  const labelEl = (
    <span className="text-sm font-medium">
      {field.label}{' '}
      {field.required ? <span className="text-status-error" aria-hidden>*</span> : null}
      <span className="ml-1 font-mono text-xs text-ink-muted">({field.key})</span>
    </span>
  );

  switch (field.type) {
    case 'text':
      return (
        <label className="block space-y-1">
          {labelEl}
          <textarea
            value={value === null ? '' : String(value)}
            onChange={(e) => onChange(field.key, e.target.value)}
            required={field.required}
            disabled={disabled}
            rows={3}
            className={baseInputCls}
          />
        </label>
      );
    case 'url':
      return (
        <label className="block space-y-1">
          {labelEl}
          <input
            type="url"
            value={value === null ? '' : String(value)}
            onChange={(e) => onChange(field.key, e.target.value)}
            required={field.required}
            disabled={disabled}
            placeholder="https://..."
            className={baseInputCls}
          />
        </label>
      );
    case 'number':
      return (
        <label className="block space-y-1">
          {labelEl}
          <input
            type="number"
            value={value === null || value === '' ? '' : String(value)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                onChange(field.key, null);
              } else {
                const n = Number(raw);
                onChange(field.key, Number.isNaN(n) ? null : n);
              }
            }}
            required={field.required}
            disabled={disabled}
            className={baseInputCls}
          />
        </label>
      );
    case 'boolean':
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(field.key, e.target.checked)}
            disabled={disabled}
            className={checkboxCls()}
          />
          {labelEl}
        </label>
      );
    case 'date':
      return (
        <label className="block space-y-1">
          {labelEl}
          <input
            type="date"
            value={value === null ? '' : String(value)}
            onChange={(e) => onChange(field.key, e.target.value || null)}
            required={field.required}
            disabled={disabled}
            className={baseInputCls}
          />
        </label>
      );
    case 'string':
    default:
      return (
        <label className="block space-y-1">
          {labelEl}
          <input
            type="text"
            value={value === null ? '' : String(value)}
            onChange={(e) => onChange(field.key, e.target.value)}
            required={field.required}
            disabled={disabled}
            className={baseInputCls}
          />
        </label>
      );
  }
}

/**
 * Detecta keys en `values` que no estan en el schema actual — datos huerfanos
 * de un schema anterior. Lo usamos para mostrarlos como "campos no
 * reconocidos" y permitir limpiarlos manualmente.
 */
export function findOrphanKeys(
  schema: FormSchema,
  values: DynamicFormValues,
): string[] {
  const known = new Set(schema.fields.map((f) => f.key));
  return Object.keys(values).filter((k) => !known.has(k));
}

/**
 * Lee `details.type_parameters` con tolerancia a shapes raras. Devuelve un
 * objeto plano con los valores tipados como DynamicFormValue.
 */
export function readTypeParameters(
  details: Record<string, unknown> | null | undefined,
): DynamicFormValues {
  if (!details || typeof details !== 'object') return {};
  const raw = (details as { type_parameters?: unknown }).type_parameters;
  if (!raw || typeof raw !== 'object') return {};
  const out: DynamicFormValues = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v === null) {
      out[k] = null;
    } else if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    } else {
      // Shape no soportada (objeto/array) — la dejamos como string serializado
      // para que el usuario al menos vea el dato y pueda limpiarlo.
      out[k] = JSON.stringify(v);
    }
  }
  return out;
}
